import React from 'react';
import {IPropsInjectedByViewerScript} from '../types/app-types';
import {IHostProps, ISantaProps} from '@wix/native-components-infra/dist/es/src/types/types';
import {BackInStockStoreProps} from '../viewerScript/stores/BackInStockStore';
import {useStyles} from '@wix/tpa-settings/react';
import {useExperiments as experimentHook} from '@wix/yoshi-flow-editor';

export const PropsContext = React.createContext<ProvidedGlobalProps>(undefined);

export interface ProvidedGlobalProps {
  globals?: GlobalProps;
}

export type GlobalProps = Pick<
  IPropsInjectedByViewerScript,
  | 'addedToCartStatus'
  | 'addToCartState'
  | 'breakdownParams'
  | 'reportBI'
  | 'closeWixModal'
  | 'cashierExpressCheckoutWidgetProps'
  | 'cashierExpressEstimatedTotal'
  | 'currentPrice'
  | 'formatPrice'
  | 'dynamicPaymentsMethodsAmount'
  | 'errors'
  | 'experiments'
  | 'handleAddToCart'
  | 'handleBuyNow'
  | 'handleOpenModal'
  | 'handleSubscribe'
  | 'handleUserInput'
  | 'handleWishlistButtonClick'
  | 'hasMultipleMedia'
  | 'hideNavigationUrls'
  | 'htmlTags'
  | 'infoSection'
  | 'isDesktop'
  | 'isEditorMode'
  | 'isEditorX'
  | 'isInteractive'
  | 'isMobile'
  | 'isAllowedToCheckout'
  | 'isProductSubmitted'
  | 'isQuickView'
  | 'isRTL'
  | 'isResponsive'
  | 'isSEO'
  | 'isSSR'
  | 'isMobileOnDesktopMode'
  | 'isPreOrderState'
  | 'layoutId'
  | 'modalState'
  | 'navigate'
  | 'navigateToLink'
  | 'onAppLoaded'
  | 'reportProductPageLoadedPanorama'
  | 'pagePath'
  | 'pagination'
  | 'product'
  | 'productGroup'
  | 'productUrl'
  | 'productUrlBySlug'
  | 'productWasAddedToWishlist'
  | 'quantityRange'
  | 'remainingItemCount'
  | 'resetAddedToCartStatus'
  | 'resetWishlistStatus'
  | 'selectedVariant'
  | 'setDynamicPaymentsMethodsAmount'
  | 'shouldFocusAddToCartButton'
  | 'shouldShowAddToCartSuccessAnimation'
  | 'hasPaymentOptionsBreakdown'
  | 'hasDiscountName'
  | 'shouldShowSubscribeButton'
  | 'shouldShowSubscriptionPlans'
  | 'shouldShowWishlistButton'
  | 'shouldShorUserInputs'
  | 'slots'
  | 'dynamicPaymentMethodsTheme'
  | 'siteUrl'
  | 'socialSharing'
  | 'subscriptionPlans'
  | 'subscriptionPlanVariantPrices'
  | 'priceBreakdown'
  | 'texts'
  | 'userInputErrors'
  | 'scrollAndFocusInputTarget'
  | 'userInputs'
  | 'validateUserInputsChanges'
  | 'isUserInputsValid'
  | 'variantInfo'
  | 'instanceId'
  | 'wishlistActionStatus'
  | 'withModalGallery'
  | 'styles'
  | 'labelOverrides'
  | 'useExperiments'
  | 'toastErrorMessage'
  | 'isCombinedListing'
  | keyof BackInStockStoreProps
> &
  Pick<IHostProps, 'updateLayout' | 'dimensions' | 'style' | 'accessibilityEnabled' | 'LazySentry'> & {
    host: IHostProps;
  };

export function withGlobalPropsProvider(Component) {
  // eslint-disable-next-line react/display-name
  return (props: IPropsInjectedByViewerScript & ISantaProps & {style: IHostProps['style']}) => {
    const {
      addToCartState,
      addedToCartStatus,
      breakdownParams,
      reportBI,
      cashierExpressCheckoutWidgetProps,
      cashierExpressEstimatedTotal,
      closeWixModal,
      currentPrice,
      formatPrice,
      dynamicPaymentsMethodsAmount,
      errors,
      experiments,
      handleAddToCart,
      handleBuyNow,
      handleNotifyWhenBackInStock,
      handleOpenModal,
      handleSubscribe,
      handleUserInput,
      handleWishlistButtonClick,
      hasMultipleMedia,
      hideNavigationUrls,
      htmlTags,
      infoSection,
      isBackInStockEnabled,
      isBackInStockLoading,
      isDesktop,
      isEditorMode,
      isEditorX,
      isInteractive,
      isMobile,
      isAllowedToCheckout,
      isProductSubmitted,
      isQuickView,
      isRTL,
      isResponsive,
      isSEO,
      isSSR,
      isMobileOnDesktopMode,
      isPreOrderState,
      layoutId,
      modalState,
      navigate,
      navigateToLink,
      dynamicPaymentMethodsTheme,
      onAppLoaded,
      reportProductPageLoadedPanorama,
      pagePath,
      pagination,
      product,
      productGroup,
      isCombinedListing,
      productUrl,
      productUrlBySlug,
      productWasAddedToWishlist,
      quantityRange,
      remainingItemCount,
      resetAddedToCartStatus,
      resetWishlistStatus,
      selectedVariant,
      setDynamicPaymentsMethodsAmount,
      shouldFocusAddToCartButton,
      shouldShowAddToCartSuccessAnimation,
      hasPaymentOptionsBreakdown,
      hasDiscountName,
      shouldShowSubscribeButton,
      shouldShowSubscriptionPlans,
      shouldShowWishlistButton,
      shouldShorUserInputs,
      siteUrl,
      socialSharing,
      style,
      subscriptionPlans,
      subscriptionPlanVariantPrices,
      priceBreakdown,
      texts,
      userInputErrors,
      scrollAndFocusInputTarget,
      userInputs,
      validateUserInputsChanges,
      isUserInputsValid,
      variantInfo,
      instanceId,
      wishlistActionStatus,
      withModalGallery,
      labelOverrides,
      useExperiments,
      toastErrorMessage,
      ...passThroughProps
    } = props;

    const {dimensions, updateLayout, accessibilityEnabled, LazySentry} = props.host;
    const styles = useStyles();
    const experimentsHook = experimentHook().experiments;

    const globals = {
      accessibilityEnabled,
      addToCartState,
      addedToCartStatus,
      breakdownParams,
      reportBI,
      cashierExpressCheckoutWidgetProps,
      cashierExpressEstimatedTotal,
      closeWixModal,
      currentPrice,
      formatPrice,
      dimensions,
      dynamicPaymentsMethodsAmount,
      errors,
      experiments,
      handleAddToCart,
      handleBuyNow,
      handleNotifyWhenBackInStock,
      handleOpenModal,
      handleSubscribe,
      handleUserInput,
      handleWishlistButtonClick,
      hasMultipleMedia,
      hideNavigationUrls,
      htmlTags,
      infoSection,
      isBackInStockEnabled,
      isBackInStockLoading,
      isDesktop,
      isEditorMode,
      isEditorX,
      isInteractive,
      isMobile,
      isAllowedToCheckout,
      isProductSubmitted,
      isQuickView,
      isRTL,
      isResponsive,
      isSEO,
      isSSR,
      isMobileOnDesktopMode,
      isPreOrderState,
      layoutId,
      modalState,
      navigate,
      navigateToLink,
      onAppLoaded,
      reportProductPageLoadedPanorama,
      pagePath,
      pagination,
      product,
      productGroup,
      productUrl,
      productUrlBySlug,
      productWasAddedToWishlist,
      quantityRange,
      remainingItemCount,
      resetAddedToCartStatus,
      resetWishlistStatus,
      selectedVariant,
      setDynamicPaymentsMethodsAmount,
      shouldFocusAddToCartButton,
      shouldShowAddToCartSuccessAnimation,
      hasPaymentOptionsBreakdown,
      hasDiscountName,
      isCombinedListing,
      shouldShowSubscribeButton,
      shouldShowSubscriptionPlans,
      shouldShowWishlistButton,
      shouldShorUserInputs,
      dynamicPaymentMethodsTheme,
      siteUrl,
      slots: passThroughProps.slots,
      socialSharing,
      style,
      styles,
      LazySentry,
      subscriptionPlans,
      subscriptionPlanVariantPrices,
      priceBreakdown,
      texts,
      updateLayout,
      userInputErrors,
      scrollAndFocusInputTarget,
      userInputs,
      validateUserInputsChanges,
      isUserInputsValid,
      variantInfo,
      instanceId,
      wishlistActionStatus,
      withModalGallery,
      labelOverrides,
      useExperiments: experimentsHook,
      toastErrorMessage,
      host: props.host,
    };

    return (
      <PropsContext.Provider value={{globals}}>
        <Component {...passThroughProps} />
      </PropsContext.Provider>
    );
  };
}

export function withGlobalProps<T>(Component: any): React.FunctionComponent<React.PropsWithChildren<T>> | any {
  // eslint-disable-next-line react/display-name
  return (props) => {
    return <PropsContext.Consumer>{(globalProps) => <Component {...globalProps} {...props} />}</PropsContext.Consumer>;
  };
}

export function useGlobals(): GlobalProps | undefined {
  const context = React.useContext(PropsContext);
  return context?.globals;
}
